/* eslint-disable sonarjs/no-identical-functions */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-console */
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  RACTextbox,
  RACSelect,
  RACButton,
  CircularProgress,
  IconButton,
} from '@rentacenter/racstrap';
import {
  Attachments,
  EscalationOption,
  FormField,
  ReferenceKeys,
} from '../../../types/types';
import {
  useCustomerServiceDispatch,
  useCustomerServiceState,
} from '../../../context/CustomerService/CustomerServiceProvider';
import { useUserStateContext } from '../../../context/user/user-contexts';
import { customerStyles } from '../../customerStyles';
import {
  buildUploadPayload,
  computeRequiredFields,
  decodeAndTriggerDownload,
  fetchData,
  fetchEscalatedData,
  formatPhone,
  getCategoryIdByValue,
  getFilteredActions,
  getFilteredSubcategories,
  getSubCategoryIdByValue,
  gridStyle,
  isValidReferenceKey,
  mapInitialInfo,
  prepareEditApiPayload,
  readFileAsBase64,
  removeEmptyFields,
  toOptions,
  uniqueifyFilenames,
  validateRequiredFields,
} from '../../shared/utils';
import { ReactComponent as Attachment } from '../../../assets/images/attachment-icon.svg';
import {
  requiredFields as baseRequiredFields,
  CSM_REQUEST_ERROR_MESSAGE,
  CSM_REQUEST_ERROR_MESSAGE_2,
  CSM_SUCCESS_MESSAGE_2,
  CSM_SUCCESS_MESSAGE_3,
  editfields,
} from '../../../constants/constants';
import { ApiSuccessModal } from '../../../common/ApiSuccess/ApiSuccess';
import { useParams } from 'react-router-dom';
import { attachmentsUrl, maganeIncident } from '../../../api/user';
import { SearchableDropdown } from '../../shared/SearchableDropdown';
import CustomTextArea from '../../shared/CustomTextArea';
import { CustomDropdown } from '../../shared/CustomDropdown';
import { useReferenceContext } from '../../../context/CustomerService/ReferenceProvider';
import { Params } from '../../../utils/utils';
import { KeyboardArrowUp, KeyboardArrowDown } from '@material-ui/icons';
import Resolnformation from './Resolnformation';
import Activity from './Activity';

const BasicInformation: React.FC = () => {
  const classes = customerStyles();
  const { incident, disable, InitialTktInfo } = useCustomerServiceState();
  const { references, loading } = useReferenceContext();
  const { user, isPending } = useUserStateContext();
  const { updateIncidentSection, setDisabled, updateInitialTktInfo } =
    useCustomerServiceDispatch();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [screenLoader, setScreenLoader] = useState<boolean>(false);
  const [isApiSuccessOpen, setIsApiSuccessOpen] = useState(false);
  const [apiMessage, setApiMessage] = useState('');
  const [apiSubMessage, setApiSubMessage] = useState('');
  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [showCancel, setShowCancel] = useState<boolean>(false);
  const [escalationOptions, setEscalationOptions] = useState<
    EscalationOption[]
  >([{ label: 'Select', value: '' }]);
  const [escalationLoading, setEscalationLoading] = useState(false);
  const [isAssignmentSelected, setIsAssignmentSelected] = useState(false);
  const [isEscalationSelected, setIsEscalationSelected] = useState(false);
  const { ticketId } = useParams<Params>();
  const [displaySearch, setDisplaySearch] = useState<boolean>(true);

  const selectedCategoryId = useMemo(
    () =>
      getCategoryIdByValue(
        references?.category as any,
        incident.basicInfo.category
      ),
    [references?.category, incident.basicInfo.category]
  );

  const selectedSubCategoryId = useMemo(
    () =>
      getSubCategoryIdByValue(
        references?.sub_category as any,
        incident.basicInfo.sub_category
      ),
    [references?.sub_category, incident.basicInfo.sub_category]
  );

  const editButton = !!incident.basicInfo.ticketNumber;
  const editButtonVisible = !!incident.basicInfo.ticketNumber && disable;
  const isEditDisabled = !!incident.assignment.escalatedTo;
  const removedSubmit = editButtonVisible;

  const storeOptions = useMemo(
    () =>
      user?.stores?.map((store: any) => ({
        label: `${store.accountingUnitNumber} - ${store.storeType} - ${
          store.addressLine1
        } - ${store.city} - ${store.state} -- ${formatPhone(
          store.workPhoneNumber
        )}`,
        value: store.accountingUnitNumber,
      })) ?? [],
    [user?.stores]
  );

  const filteredSubCategoryOptions = useMemo(() => {
    const raw =
      editButton && disable
        ? (references?.sub_category as any)
        : getFilteredSubcategories(
            selectedCategoryId,
            references?.sub_category as any
          );
    return toOptions(raw);
  }, [editButton, disable, references?.sub_category, selectedCategoryId]);

  const filteredActionOptions = useMemo(() => {
    const raw =
      editButton && disable
        ? (references?.action_type as any)
        : getFilteredActions(
            selectedSubCategoryId,
            references?.action_type as any
          );
    return toOptions(raw);
  }, [editButton, disable, references?.action_type, selectedSubCategoryId]);

  const dynamicRequiredFields = useMemo(
    () =>
      computeRequiredFields({
        baseRequiredFields,
        basicInfo: incident.basicInfo as any,
        references: references as any,
        selectedCategoryId,
        selectedSubCategoryId,
        filteredSubCategoryOptions,
        filteredActionOptions,
      }),
    [
      incident.basicInfo,
      references,
      selectedCategoryId,
      selectedSubCategoryId,
      filteredSubCategoryOptions,
      filteredActionOptions,
    ]
  );

  const isSubmitEnabled = useMemo(
    () =>
      validateRequiredFields(
        dynamicRequiredFields as any,
        incident.basicInfo as any
      ),
    [dynamicRequiredFields, incident.basicInfo]
  );

  useEffect(() => {
    if (!ticketId || incident.basicInfo.ticketNumber) return;

    setScreenLoader(true);

    fetchData(
      ticketId,
      updateIncidentSection,
      setScreenLoader,
      updateInitialTktInfo
    ).then(({ escalationOptions }) => {
      if (escalationOptions) {
        setEscalationOptions(escalationOptions);
      }
    });

    setDisabled(true);
  }, [
    ticketId,
    updateIncidentSection,
    setDisabled,
    updateInitialTktInfo,
    incident.basicInfo.ticketNumber,
  ]);

  const handleChange = useCallback(
    (name: FormField, value: string) => {
      updateIncidentSection('basicInfo', { [name]: value });
      if (name === 'category') {
        updateIncidentSection('basicInfo', {
          sub_category: '',
          action_type: '',
        });
      }
      if (name === 'sub_category') {
        updateIncidentSection('basicInfo', { action_type: '' });
      }
    },
    [updateIncidentSection]
  );

  const handleAssignmentChange = useCallback(
    (name: FormField, value: string) => {
      updateIncidentSection('assignment', { [name]: value });
    },
    [updateIncidentSection]
  );

  const handleButtonClick = useCallback(
    () => fileInputRef.current?.click(),
    []
  );

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const existingAttachments = incident.basicInfo.attachments ?? [];
      const uniqueFiles = uniqueifyFilenames(
        Array.from(files),
        existingAttachments.map((a) => a.attachmentName)
      );

      try {
        setScreenLoader(true);
        const payload = await buildUploadPayload(uniqueFiles, readFileAsBase64);
        const result = await attachmentsUrl(payload);
        if (result && Array.isArray(result.attachments)) {
          const formattedAttachments = result.attachments.map(
            (item: { documentName: string; documentLocation: string }) => ({
              attachmentName: item.documentName,
              attachmentLocation: item.documentLocation,
            })
          );
          updateIncidentSection('basicInfo', {
            attachments: [...existingAttachments, ...formattedAttachments],
          });
        } else {
          console.warn('Unexpected response format:', result);
        }
      } catch (error) {
        console.error('Failed to upload and store attachments:', error);
      } finally {
        setScreenLoader(false);
        if (event.target) event.target.value = '';
      }
    },
    [incident.basicInfo.attachments, updateIncidentSection]
  );

  const handleDownload = useCallback(async (file: Attachments) => {
    try {
      setScreenLoader(true);
      const payload = {
        documents: [
          {
            documentLocation: file.attachmentLocation,
            documentName: file.attachmentName,
          },
        ],
        type: 'download',
      } as const;
      const response = await attachmentsUrl(payload);
      const fileData = Array.isArray(response) ? response[0] : response;
      const doc = fileData?.documents?.[0];
      if (!doc?.documentBase64 || !doc?.documentName)
        throw new Error('Invalid file data received');
      await decodeAndTriggerDownload(
        doc.documentBase64,
        doc.documentName,
        doc.fileType
      );
    } catch (error) {
      console.error('Failed to download attachment:', error);
    } finally {
      setScreenLoader(false);
    }
  }, []);

  const sanitizeInput = (name: string, value: string) =>
    ['firstName', 'lastName'].includes(name)
      ? value?.replace(/[^A-Z-',.; a-z]/gi, '')
      : value;

  const getMaxLength = (name: string) =>
    ((
      {
        agreementnumber: 19,
        firstName: 30,
        lastName: 30,
        callback: 14,
        alterphonenumb: 14,
      } as Record<string, number>
    )[name] ?? 30);

  const getType = (name: string) =>
    ['callback', 'alterphonenumb'].includes(name) ? 'phoneno' : 'text';
  const getPattern = (name: string) =>
    ['callback', 'alterphonenumb'].includes(name) ? '\\D*' : undefined;

  const handleSubmit = useCallback(async () => {
    try {
      setScreenLoader(true);
      const apiPayload = removeEmptyFields(
        prepareEditApiPayload(incident, user)
      );
      const res = await maganeIncident(apiPayload);
      if (res?.ticketId && res?.ticketNumber) {
        setApiMessage(CSM_SUCCESS_MESSAGE_3);
        setApiSubMessage(`${CSM_SUCCESS_MESSAGE_2} ${res.ticketNumber}`);
        setIsApiSuccessOpen(true);
        setIsApiError(false);
        setDisabled(true);
        setShowCancel(false);
      } else {
        setIsApiError(true);
        setApiMessage(CSM_REQUEST_ERROR_MESSAGE);
        setApiSubMessage(CSM_REQUEST_ERROR_MESSAGE_2);
        setIsApiSuccessOpen(true);
      }
    } catch (error) {
      setIsApiError(true);
      setApiMessage(CSM_REQUEST_ERROR_MESSAGE);
      setApiSubMessage(CSM_REQUEST_ERROR_MESSAGE_2);
      setIsApiSuccessOpen(true);
      console.error('Error submitting ticket:', error);
    } finally {
      setScreenLoader(false);
    }
  }, [incident, setDisabled, user]);

  const handleEdit = () => {
    setDisabled(false);
    setShowCancel(true);
  };

  const handleCancel = () => {
    setDisabled(true);
    setShowCancel(false);
    mapInitialInfo(InitialTktInfo, updateIncidentSection);
  };

  const toggleFilterPanel = () => {
    const next = !displaySearch;
    setDisplaySearch(next);
  };

  return (
    <>
      {isApiSuccessOpen && (
        <ApiSuccessModal
          open={isApiSuccessOpen}
          message={apiMessage}
          secondmsg={apiSubMessage}
          error={isApiError}
          onClose={() => {
            setApiMessage('');
            setApiSubMessage('');
            setIsApiSuccessOpen(false);
            setIsApiError(false);
            if (!isApiError) {
              setScreenLoader(true);
              fetchData(
                ticketId,
                updateIncidentSection,
                setScreenLoader,
                updateInitialTktInfo
              ).then(({ escalationOptions }) => {
                if (escalationOptions) {
                  setEscalationOptions(escalationOptions);
                }
              });
              setDisabled(true);
            }
          }}
        />
      )}

      {screenLoader ? (
        <Grid className={classes.masterLoader}>
          <Grid className={classes.Loader}>
            <CircularProgress />
          </Grid>
        </Grid>
      ) : null}

      <Grid
        container
        id="bsicinfo"
        className={`${classes.mb16} ${classes.px2}`}
      >
        <Grid item md={12}>
          <Card className={`${classes.card} ${classes.w100}`}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item md={6}>
                  <Typography className={classes.title}>
                    Basic Information
                  </Typography>
                </Grid>
                <Grid
                  item
                  md={6}
                  container
                  justifyContent="flex-end"
                  alignItems="flex-start"
                >
                  <IconButton onClick={toggleFilterPanel} size="small">
                    {displaySearch ? (
                      <KeyboardArrowUp />
                    ) : (
                      <KeyboardArrowDown />
                    )}
                  </IconButton>
                </Grid>
              </Grid>
              {displaySearch && (
                <Grid container spacing={3} justifyContent="space-between">
                  {editfields.map(
                    ({ name, label, disabled: isFieldDisabled = false }) => {
                      const value = (incident.basicInfo as any)[name] ?? '';
                      const isRequired = dynamicRequiredFields.includes(
                        name as any
                      );

                      if (name === 'storenumber') {
                        return (
                          <Grid
                            item
                            lg={4}
                            md={4}
                            sm={4}
                            key={name}
                            style={gridStyle}
                          >
                            <SearchableDropdown
                              options={storeOptions}
                              selection={async (val) => {
                                handleChange(name, val);
                                if (!val) {
                                  setEscalationOptions([
                                    { label: 'Select', value: '' },
                                  ]);
                                  setIsEscalationSelected(false);
                                } else {
                                  if (!incident.assignment?.assignmentGroup) {
                                    setEscalationLoading(true);
                                    const options = await fetchEscalatedData(
                                      val
                                    );
                                    setEscalationOptions(options);
                                    setEscalationLoading(false);
                                  }
                                }
                              }}
                              defaultValue={value as string}
                              loading={isPending}
                              inputLabel={label}
                              required={isRequired}
                              isDisabled={disable}
                            />
                          </Grid>
                        );
                      }

                      if (name === 'category') {
                        const categoryOptions = toOptions(
                          (references as any)[name as ReferenceKeys]
                        );
                        return (
                          <Grid
                            item
                            lg={4}
                            md={4}
                            sm={4}
                            key={name}
                            style={gridStyle}
                          >
                            <CustomDropdown
                              inputLabel={label}
                              loading={loading}
                              required={isRequired}
                              options={categoryOptions}
                              defaultValue={value as string}
                              isDisabled={disable}
                              onChange={(e: any) => {
                                handleChange(name, e);
                              }}
                            />
                          </Grid>
                        );
                      }

                      if (name === 'sub_category') {
                        const hasSubs = filteredSubCategoryOptions.length > 0;
                        return (
                          <Grid
                            item
                            lg={4}
                            md={4}
                            sm={4}
                            key={name}
                            style={gridStyle}
                          >
                            <CustomDropdown
                              inputLabel={label}
                              loading={loading}
                              required={isRequired}
                              options={filteredSubCategoryOptions}
                              defaultValue={value as string}
                              isDisabled={
                                disable ? true : !selectedCategoryId || !hasSubs
                              }
                              onChange={(e: any) => {
                                handleChange(name, e);
                              }}
                            />
                          </Grid>
                        );
                      }

                      if (name === 'action_type') {
                        const hasActions = filteredActionOptions.length > 0;
                        return (
                          <Grid
                            item
                            lg={4}
                            md={4}
                            sm={4}
                            key={name}
                            style={gridStyle}
                          >
                            <CustomDropdown
                              inputLabel={label}
                              loading={loading}
                              required={isRequired}
                              options={filteredActionOptions}
                              defaultValue={value as string}
                              isDisabled={
                                disable
                                  ? true
                                  : !selectedSubCategoryId || !hasActions
                              }
                              onChange={(e: any) => {
                                handleChange(name, e);
                              }}
                            />
                          </Grid>
                        );
                      }
                      if (name === 'shortDescription') {
                        return (
                          <>
                            <Grid item xs={8} />
                            <Grid
                              item
                              lg={5}
                              md={5}
                              sm={5}
                              key={name}
                              style={gridStyle}
                            >
                              <Typography className={classes.notesLabel}>
                                {label}{' '}
                                {isRequired && (
                                  <span className={`${classes.reqField}`}>
                                    *
                                  </span>
                                )}
                              </Typography>
                              <CustomTextArea
                                value={value as string}
                                onChange={(e: any) => {
                                  handleChange(name, e);
                                }}
                                minHeight={100}
                                maxHeight={300}
                                disabled={disable}
                              />
                            </Grid>
                          </>
                        );
                      }

                      if (name === 'description') {
                        return (
                          <Grid
                            item
                            lg={7}
                            md={7}
                            sm={7}
                            key={name}
                            style={gridStyle}
                          >
                            <Typography className={classes.notesLabel}>
                              {label}{' '}
                              {isRequired && (
                                <span className={`${classes.reqField}`}>*</span>
                              )}
                            </Typography>
                            <CustomTextArea
                              value={value as string}
                              onChange={(e: any) => {
                                handleChange(name, e);
                              }}
                              minHeight={100}
                              maxHeight={300}
                              disabled={disable}
                            />
                          </Grid>
                        );
                      }

                      if (isValidReferenceKey(name as string)) {
                        const opts = toOptions(
                          (references as any)[name as ReferenceKeys]
                        );
                        return (
                          <Grid
                            item
                            lg={4}
                            md={4}
                            sm={4}
                            key={name}
                            style={gridStyle}
                          >
                            <CustomDropdown
                              inputLabel={label}
                              loading={loading}
                              required={isRequired}
                              options={opts}
                              defaultValue={value as string}
                              isDisabled={disable}
                              onChange={(e: any) => {
                                handleChange(name, e);
                              }}
                            />
                          </Grid>
                        );
                      }

                      return (
                        <Grid
                          item
                          lg={4}
                          md={4}
                          sm={4}
                          key={name}
                          style={gridStyle}
                        >
                          <RACTextbox
                            type={getType(name)}
                            name={name}
                            inputlabel={label}
                            maxlength={getMaxLength(name)}
                            pattern={getPattern(name)}
                            isCurrency={false}
                            disabled={isFieldDisabled || disable}
                            required={isRequired}
                            value={value as string}
                            OnChange={(e: any) =>
                              handleChange(
                                name,
                                sanitizeInput(name, e.target.value)
                              )
                            }
                          />
                        </Grid>
                      );
                    }
                  )}
                  <Grid
                    container
                    id="assignmentinfo"
                    className={`${classes.mb16} ${classes.px2}`}
                  >
                    <Grid item md={12}>
                      <Typography
                        className={`${classes.title} ${classes.mb15}`}
                      >
                        Assignment Information
                      </Typography>
                    </Grid>
                    <Grid container spacing={2} justifyContent="space-between">
                      <Grid item lg={4} md={4} sm={4} style={gridStyle}>
                        <RACSelect
                          name="assignmentGroup"
                          inputLabel="Assignment Group"
                          loading={loading}
                          options={toOptions(references?.assignment_group)}
                          defaultValue={
                            incident.assignment?.assignmentGroup ?? ''
                          }
                          isDisabled={
                            isEscalationSelected ||
                            !!incident.assignment?.escalatedTo ||
                            disable
                          }
                          onChange={(e: any) => {
                            handleAssignmentChange(
                              'assignmentGroup',
                              e.target.value
                            );
                            setIsAssignmentSelected(!!e.target.value);
                            setIsEscalationSelected(false);
                          }}
                        />
                      </Grid>
                      <Grid item lg={4} md={4} sm={4} style={gridStyle}>
                        {incident.assignment.assigned ? (
                          <RACTextbox
                            type="text"
                            name="escalatedTo"
                            inputlabel="Escalated to"
                            isCurrency={false}
                            disabled={true}
                            value={`${incident.assignment?.role} - ${incident.assignment?.esName}`}
                          />
                        ) : (
                          <RACSelect
                            name="escalatedTo"
                            inputLabel="Escalated to"
                            loading={escalationLoading}
                            options={escalationOptions}
                            isDisabled={
                              isAssignmentSelected ||
                              !!incident.assignment?.assignmentGroup ||
                              disable
                            }
                            defaultValue={
                              incident.assignment?.escalatedTo ?? ''
                            }
                            onChange={(e: any) => {
                              const selectedValue = e.target.value;
                              const selectedOption = escalationOptions.find(
                                (opt) => opt.value === selectedValue
                              );
                              handleAssignmentChange(
                                'escalatedTo',
                                selectedValue
                              );
                              handleAssignmentChange(
                                'esName',
                                selectedOption?.esName ?? ''
                              );
                              handleAssignmentChange(
                                'role',
                                selectedOption?.role ?? ''
                              );
                              handleAssignmentChange(
                                'escalationContact',
                                selectedOption?.phone_number ?? 'none'
                              );
                            }}
                          />
                        )}
                      </Grid>
                      <Grid item lg={4} md={4} sm={4} style={gridStyle}>
                        <RACTextbox
                          type="text"
                          name="escalationContact"
                          inputlabel="Escalation Contact"
                          isCurrency={false}
                          disabled={true}
                          value={incident.assignment?.escalationContact ?? ''}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={2}
                      className={classes.escaltedinfo}
                    >
                      <Grid item lg={4} md={4} sm={4} style={gridStyle}>
                        <RACTextbox
                          type="text"
                          name="escalationContact"
                          inputlabel="Comment"
                          isCurrency={false}
                          disabled={disable}
                          value={incident.assignment?.comment ?? ''}
                          OnChange={(e: any) => {
                            handleAssignmentChange('comment', e.target.value);
                          }}
                        />
                      </Grid>
                      <Grid item lg={4} md={4} sm={4} style={gridStyle}>
                        <RACButton
                          variant="outlined"
                          color="secondary"
                          id="uploadDocument"
                          className={classes.uploadButton}
                          onClick={handleButtonClick}
                          disabled={disable}
                          startIcon={<Attachment />}
                        >
                          Add Attachments
                          <input
                            ref={fileInputRef}
                            multiple
                            type="file"
                            onChange={handleFileUpload}
                            accept=".pdf,image/*,.txt,.xlsx,.xls,.doc,.docx,.mp4,.avi"
                            style={{ display: 'none' }}
                          />
                        </RACButton>
                      </Grid>
                      <Grid
                        item
                        lg={4}
                        md={4}
                        sm={4}
                        style={{
                          ...gridStyle,
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 8,
                        }}
                      >
                        {showCancel && (
                          <RACButton
                            className={`${classes.btn} ${classes.btn1}`}
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={handleCancel}
                          >
                            Cancel
                          </RACButton>
                        )}
                        {editButtonVisible && (
                          <RACButton
                            className={`${classes.btn} ${classes.btn1}`}
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={handleEdit}
                            disabled={isEditDisabled}
                          >
                            Edit
                          </RACButton>
                        )}
                        {!removedSubmit && (
                          <RACButton
                            className={classes.btn}
                            variant="contained"
                            size="small"
                            color="primary"
                            disabled={!isSubmitEnabled}
                            onClick={handleSubmit}
                          >
                            Submit
                          </RACButton>
                        )}
                      </Grid>
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        style={{
                          marginTop: '20px',
                          paddingLeft: '50px',
                          flexWrap: 'nowrap',
                        }}
                      >
                        {(incident?.basicInfo?.attachments?.length ?? 0) >
                          0 && (
                          <Grid
                            item
                            style={{
                              display: 'flex',
                              gap: '10px',
                              flexWrap: 'wrap',
                            }}
                          >
                            {incident.basicInfo.attachments?.map(
                              (file, index) => (
                                <Typography
                                  key={index}
                                  variant="body2"
                                  style={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                  }}
                                  onClick={() => handleDownload(file)}
                                >
                                  {file.attachmentName}
                                </Typography>
                              )
                            )}
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Resolnformation />
      <Activity />
    </>
  );
};

export default BasicInformation;
